import React, { useState, useEffect } from 'react';
import { PROFESSIONS, fetchHeadlineTrades, FALLBACK_TRADES, FALLBACK_AFFINITY, PlatformAffinityStrip, PLATFORM_META, UPCOMING_PLATFORM_META } from './Home';

/*
 * HomeV2 — the "profession-playbook" front door (the A/B "B").
 * Flow: two doors -> smart profession/URL input -> instant per-profession
 * "playbook" (where/when/top idea) -> a 90%-built post whose "finish & publish"
 * = signup. See the project_profession_playbook_funnel memory for the full
 * concept + the real architecture (cached profession_playbooks, signup
 * rearrange — both on the auth/backend track).
 *
 * The playbook is REAL, server-generated data: /api/capture-business resolves
 * the trade and returns a cached-or-AI-generated playbook (the profession_playbook
 * table). There is NO hardcoded playbook fallback — if the server can't build
 * one we say so honestly rather than show invented data. Mounted at /v2 (App.js);
 * the current home page stays at / untouched.
 */

const BRAND = '#00aef7';

/* The playbook now comes entirely from the server (captured.playbook, generated
   + cached in profession_playbook). The old hardcoded PLAYBOOKS / FALLBACK /
   normalize() sample data was removed — nothing is ever read from fake data. */

const DOORS = [
  { id: 'new', title: 'My business is new to social media and I need help to get started', sub: 'I barely post — show me an easier way' },
  { id: 'ideas', title: "I post for my business, but I'm out of time, patience and ideas", sub: 'Give me ideas that actually work' },
];


function DoorIcon({ id }) {
  if (id === 'new') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7" aria-hidden="true">
        <path d="M12 22V11" />
        <path d="M12 12C12 8 9 6 4 6c0 5 3 7 8 6Z" />
        <path d="M12 13c0-3 3-5 8-5 0 4-3 6-8 5Z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7" aria-hidden="true">
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M12 2a6 6 0 0 0-4 10.5c1 1 1.5 2 1.5 3.5h5c0-1.5.5-2.5 1.5-3.5A6 6 0 0 0 12 2Z" />
    </svg>
  );
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Time-slot colors: slot 1 (morning) amber, slot 2 (evening) violet.
const SLICE_COLORS = [
  { bg: '#FAC775', fg: '#412402' },
  { bg: '#CECBF6', fg: '#26215C' },
];

/* One ranked, full-color brand badge for the "where to post" play. Reuses the
   brand icon set from Home.jsx (PLATFORM_META / UPCOMING_PLATFORM_META). */
function PlatformBadge({ pkey, rank }) {
  const meta = PLATFORM_META[pkey] || UPCOMING_PLATFORM_META[pkey];
  if (!meta) return null;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative w-14 h-14 rounded-2xl flex items-center justify-center text-white" style={{ background: meta.bg }}>
        <span style={{ transform: 'scale(1.85)' }}>{meta.icon}</span>
        {rank ? (
          <span className="absolute -top-2 -left-2 w-[22px] h-[22px] rounded-full text-white text-[11px] font-bold flex items-center justify-center border-2 border-white" style={{ background: BRAND }}>{rank}</span>
        ) : null}
      </div>
      <span className="text-xs text-gray-500">{meta.label}</span>
    </div>
  );
}

/* The "when to post" calendar: a generic month grid with the recommended weekday
   columns filled in brand color and dotted with each day's time slots. */
function PlaybookCalendar({ days, slices }) {
  const recCols = new Set(
    (days || [])
      .map((d) => WEEKDAYS.findIndex((w) => w.toLowerCase() === String(d).toLowerCase().slice(0, 3)))
      .filter((i) => i >= 0)
  );
  const offset = 2; // generic month that starts on a Tuesday
  const cells = [];
  for (let i = 0; i < 35; i++) {
    const col = i % 7;
    const day = i - offset + 1;
    if (day < 1 || day > 31) { cells.push(<div key={i} className="aspect-square" />); continue; }
    if (recCols.has(col)) {
      cells.push(
        <div key={i} className="aspect-square flex flex-col items-center justify-center rounded-md text-xs font-semibold text-white" style={{ background: BRAND }}>
          {day}
          <span className="flex gap-0.5 mt-0.5">
            {(slices || []).slice(0, 2).map((s, k) => (
              <i key={k} className="inline-block w-1 h-1 rounded-full" style={{ background: (SLICE_COLORS[k] || SLICE_COLORS[0]).bg }} />
            ))}
          </span>
        </div>
      );
    } else {
      cells.push(<div key={i} className="aspect-square flex items-center justify-center rounded-md text-xs text-gray-400 bg-gray-50">{day}</div>);
    }
  }
  return (
    <div className="max-w-[340px]">
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((d, i) => <div key={`h${i}`} className="text-center text-[11px] text-gray-400 py-0.5">{d[0]}</div>)}
        {cells}
      </div>
    </div>
  );
}

/* Numbered "play" wrapper — the dotted drive-rail threads the sequence together. */
function Play({ n, eyebrow, title, last, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-9 h-9 rounded-full text-white text-[15px] font-bold flex items-center justify-center flex-shrink-0" style={{ background: BRAND }}>{n}</div>
        {!last && <div className="w-0 flex-1 mt-1.5" style={{ borderLeft: '2px dashed rgba(0,174,247,0.35)' }} />}
      </div>
      <div className="flex-1 min-w-0 pb-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: '#0784b8' }}>{eyebrow}</div>
        <h3 className="text-lg font-extrabold text-gray-900 leading-tight mb-2">{title}</h3>
        {children}
      </div>
    </div>
  );
}

/* Play 1 — where to post: ranked color badges + a muted "coming soon" row. */
function WhereBody({ pb, trade }) {
  const detail = Array.isArray(pb.platformDetail) ? pb.platformDetail.filter(Boolean) : [];
  const supported = detail.filter((p) => PLATFORM_META[p.key]);
  const badges = (supported.length ? supported : detail).slice(0, 6);
  const soon = (supported.length ? detail.filter((p) => !PLATFORM_META[p.key] && UPCOMING_PLATFORM_META[p.key]) : []).slice(0, 4);
  if (badges.length === 0) {
    return <p className="text-sm text-gray-500">{(pb.platforms || []).join(', ') || '—'}</p>;
  }
  return (
    <>
      <div className="flex gap-4 flex-wrap">
        {badges.map((p, i) => <PlatformBadge key={p.key} pkey={p.key} rank={i + 1} />)}
      </div>
      {soon.length > 0 && (
        <div className="mt-3 text-xs text-gray-400">
          Strong for {trade}, coming to eHandshake soon:{' '}
          {soon.map((p) => {
            const m = UPCOMING_PLATFORM_META[p.key];
            const dot = typeof m.bg === 'string' && m.bg.startsWith('#') ? m.bg : '#888';
            return (
              <span key={p.key} className="inline-flex items-center gap-1.5 text-xs rounded-full bg-gray-100 text-gray-600 px-2.5 py-1 ml-1 mb-1 align-middle">
                <i className="inline-block w-2 h-2 rounded-full" style={{ background: dot }} />{m.label}
              </span>
            );
          })}
        </div>
      )}
    </>
  );
}

/* Play 2 — when to post: two time-slot chips + the month calendar. Falls back to
   the best_time string for playbooks generated before posting_schedule existed. */
function WhenBody({ pb }) {
  const sched = pb.postingSchedule;
  const slices = (sched && Array.isArray(sched.slices) ? sched.slices : []).slice(0, 2);
  const hasCal = sched && Array.isArray(sched.days) && sched.days.length > 0;
  return (
    <>
      {slices.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-3">
          {slices.map((s, i) => {
            const c = SLICE_COLORS[i] || SLICE_COLORS[0];
            return (
              <span key={i} className="inline-flex items-center text-[13px] px-3 py-1.5 rounded-full font-semibold" style={{ background: c.bg, color: c.fg }}>
                {s.label}{s.window ? ` · ${s.window}` : ''}
              </span>
            );
          })}
        </div>
      )}
      {hasCal ? (
        <>
          <PlaybookCalendar days={sched.days} slices={slices} />
          <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-[3px]" style={{ background: BRAND }} />post day</span>
            {pb.cadence && <span>· {pb.cadence}</span>}
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-700">{pb.bestTime || '—'}{pb.cadence ? ` · ${pb.cadence}` : ''}</p>
      )}
    </>
  );
}

/* Play 3 — what to post: focused "content idea" cards in the Design Center
   inspiration style — the post TOPIC + a sample caption (what to say), never the
   work. Card #1 is the playbook's pre-baked seed_idea (instant); + generates more
   (up to 3) via /api/playbook-idea, with left/right nav. "Use this idea" is a
   non-clickable preview of the card they'll get after signup. */
function WhatBody({ pb, trade }) {
  const MAX = 3;
  const [ideas, setIdeas] = useState(pb.seedIdea && pb.seedIdea.title ? [pb.seedIdea] : []);
  const [cur, setCur] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const canGen = ideas.length < MAX && !loading;

  const generate = async () => {
    if (!canGen) return;
    setErr(''); setLoading(true);
    try {
      const res = await fetch('/api/playbook-idea', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profession: trade, avoid: ideas.map((i) => i.title) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok || !data.idea || !data.idea.title) {
        setErr(data.message || 'Could not generate an idea. Try again.');
      } else {
        setIdeas((prev) => { const next = [...prev, data.idea]; setCur(next.length - 1); return next; });
      }
    } catch (_) {
      setErr('Could not generate an idea. Try again.');
    }
    setLoading(false);
  };

  const idea = ideas[cur];
  return (
    <div className="max-w-md">
      {idea && (
        <div className="rounded-2xl p-5" style={{ background: '#f5fbff', boxShadow: 'inset 0 0 0 1px rgba(0,174,247,0.18)' }}>
          <h4 className="text-lg font-bold text-gray-900 leading-snug">{idea.title}</h4>
          {idea.caption && (
            <p className="mt-2 text-[15px] italic text-gray-500 leading-relaxed">&ldquo;{idea.caption}&rdquo;</p>
          )}
          <div className="mt-3 text-[15px] font-bold cursor-default" style={{ color: BRAND }} title="Available when you sign up">
            Use this idea →
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={generate}
          disabled={!canGen}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white transition hover:scale-[1.02] disabled:opacity-30 disabled:hover:scale-100"
          style={{ background: BRAND }}
        >
          {loading
            ? <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            : <span className="text-base leading-none">+</span>}
          {idea ? 'Another idea' : 'Generate an idea'}
        </button>

        {ideas.length > 1 && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setCur((c) => (c - 1 + ideas.length) % ideas.length)} aria-label="Previous idea" className="text-gray-400 hover:text-gray-700 text-xl leading-none">‹</button>
            <div className="flex gap-1">
              {ideas.map((_, i) => <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === cur ? '' : 'opacity-30'}`} style={{ background: BRAND }} />)}
            </div>
            <button type="button" onClick={() => setCur((c) => (c + 1) % ideas.length)} aria-label="Next idea" className="text-gray-400 hover:text-gray-700 text-xl leading-none">›</button>
          </div>
        )}

        <span className="text-xs text-gray-400">
          {ideas.length >= MAX ? 'Sign up to generate unlimited' : `tuned to ${trade}`}
        </span>
      </div>
      {err && <div className="mt-2 text-xs text-red-600">{err}</div>}
    </div>
  );
}

/* Play 4 — your first post, pre-drafted. */
function DraftBody({ pb }) {
  if (!pb.post || (!pb.post.headline && !pb.post.caption)) {
    return <p className="text-sm text-gray-500">We'll draft your first post the moment you start.</p>;
  }
  return (
    <div className="rounded-2xl ring-1 ring-gray-200 overflow-hidden shadow-sm max-w-md">
      <div className="h-32 bg-gray-50 flex items-center justify-center text-gray-400 text-sm">{pb.post.imageLabel}</div>
      <div className="p-5">
        <span className="inline-block text-[11px] font-semibold rounded-full px-2.5 py-0.5 ring-1" style={{ color: BRAND, borderColor: 'rgba(0,174,247,0.4)' }}>draft · 90% done</span>
        <h4 className="mt-2 text-lg font-bold text-gray-900">{pb.post.headline}</h4>
        <p className="mt-1.5 text-gray-600 leading-relaxed">{pb.post.caption}</p>
      </div>
    </div>
  );
}

/* Play 5 — share it (Sharing Center). First of the product-walkthrough plays;
   more (post, design center, ... teams) get appended in PlaybookPlays as we go. */
function ShareBody({ trade }) {
  return (
    <p className="text-[15px] text-gray-700 leading-relaxed">
      One tap sends your {trade} post to every channel you're on — plus a WhatsApp or email blast to your customer list. No copy-pasting, no logging into five apps.
    </p>
  );
}

/* Assembles the ordered plays. Plays 1-4 are the per-profession data and get
   reordered by the door the visitor picked; plays 5+ are the product walkthrough
   (append to narrativePlays as we build each one out — post … through teams). */
function PlaybookPlays({ pb, trade, door }) {
  const where = { eyebrow: 'where to post', title: 'Where you should be posting', body: <WhereBody pb={pb} trade={trade} /> };
  const when = { eyebrow: 'when to post', title: 'When to post', body: <WhenBody pb={pb} /> };
  const what = { eyebrow: 'what to post', title: 'What to post', body: <WhatBody pb={pb} trade={trade} /> };
  const draft = { eyebrow: 'your first play', title: 'Your first post is 90% drawn up', body: <DraftBody pb={pb} /> };

  const dataPlays = door === 'ideas' ? [what, where, when, draft] : [where, when, what, draft];

  const narrativePlays = [
    { eyebrow: 'share it', title: 'Share it everywhere at once', body: <ShareBody trade={trade} /> },
  ];

  const plays = [...dataPlays, ...narrativePlays];
  return (
    <div className="mt-6">
      {plays.map((p, i) => (
        <Play key={i} n={i + 1} eyebrow={p.eyebrow} title={p.title} last={i === plays.length - 1}>
          {p.body}
        </Play>
      ))}
    </div>
  );
}

/* Rotating profession showcase — crossfades one profession at a time every 3s.
   Pulls the FULL canonical list (~175) live from the same endpoint V1's marquee
   uses (fetchHeadlineTrades -> allNames). The static PROFESSIONS names are only
   the first-paint fallback until that resolves (or if the fetch fails). Name
   only, no platforms. */
function RotatingProfession() {
  const [names, setNames] = useState(() => PROFESSIONS.map((p) => p.name));
  const [i, setI] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchHeadlineTrades().then(({ allNames }) => {
      if (cancelled || !Array.isArray(allNames) || allNames.length === 0) return;
      setNames(allNames);
      setI(0);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % names.length), 3000);
    return () => clearInterval(id);
  }, [names.length]);

  return (
    <div className="mt-6 relative h-9" aria-live="polite" aria-label="professions we serve">
      {names.map((name, idx) => (
        <span
          key={idx}
          aria-hidden={idx !== i}
          className={`absolute inset-0 flex items-center justify-center text-2xl font-extrabold transition-opacity duration-500 ${
            idx === i ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          style={{ color: BRAND }}
        >
          {name}
        </span>
      ))}
    </div>
  );
}

/* "Where {trade} posts" rotation — V1's platform-affinity strip, reused here
   and cycled every 3s (vs 5s on V1). Seeds with the same fallbacks V1 uses,
   then swaps to live data. Shows supported platforms + the "coming soon" set. */
function PlatformRotation() {
  const [trades, setTrades] = useState(FALLBACK_TRADES);
  const [affinity, setAffinity] = useState(FALLBACK_AFFINITY);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchHeadlineTrades().then(({ trades: t, affinity: a }) => {
      if (cancelled) return;
      if (Array.isArray(t) && t.length) setTrades(t);
      if (a && typeof a === 'object' && Object.keys(a).length) setAffinity(a);
      setIdx(0);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setIdx((n) => (n + 1) % trades.length), 3000);
    return () => clearInterval(id);
  }, [trades.length]);

  const trade = trades[idx] || trades[0];
  const platforms = affinity[(trade || '').toLowerCase()] || [];

  return (
    <div className="mt-9 flex justify-center">
      <PlatformAffinityStrip trade={trade} platforms={platforms} />
      <style>{`@keyframes heroSwap{0%{opacity:0;transform:translateY(8px)}100%{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

export default function HomeV2() {
  const [step, setStep] = useState('entry'); // entry | input | playbook
  const [door, setDoor] = useState(null);
  const [profession, setProfession] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captured, setCaptured] = useState(null);

  // The playbook is the REAL server-generated one returned by the capture
  // endpoint. No hardcoded fallback — pb may be null, in which case the playbook
  // step renders an honest "building it" state instead of invented data.
  const pb = (captured && captured.playbook) || null;
  // What the playbook copy names = the server-detected / typed trade.
  const displayTrade = (captured && captured.profession) || (pb && pb.label) || 'your business';

  const reset = () => {
    setStep('entry'); setDoor(null); setProfession(''); setWebsite('');
    setLoading(false); setError(''); setCaptured(null);
  };
  const pickDoor = (id) => { setDoor(id); setStep('input'); };

  // Real backend call: SSRF guard + blocklist + moderation + site scan. On a
  // rejection we stay on the input step and show the (deliberately boring) reason.
  const submit = async () => {
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/capture-business', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ website: website.trim(), profession: profession.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setLoading(false);
        setError(data.message || "That doesn't look like a business. Try again.");
        return;
      }
      if (data.captureToken) {
        try { localStorage.setItem('eh_capture_token', data.captureToken); } catch (_) { /* ignore */ }
      }
      setCaptured(data);
      setLoading(false);
      setStep('playbook');
    } catch (_) {
      setLoading(false);
      setError('Something went wrong. Please try again.');
    }
  };

  return (
    <main className="flex-grow bg-white">
      <div className="mx-auto w-[92%] max-w-3xl min-h-[78vh] flex flex-col justify-center py-12 md:py-16">

        {step === 'entry' && (
          <div className="text-center">
            <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-gray-400 mb-3">Built for local, small businesses</div>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.1]">
              Social media that brings in <span style={{ color: BRAND }}>customers</span> — without the work.
            </h1>
            <p className="mt-5 text-gray-500 text-lg">Which one of these sounds like you?</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4 text-left">
              {DOORS.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => pickDoor(d.id)}
                  className="group rounded-2xl ring-1 ring-gray-200 hover:ring-2 hover:ring-[#00aef7] p-6 transition-all hover:shadow-lg bg-white"
                >
                  <span style={{ color: BRAND }}><DoorIcon id={d.id} /></span>
                  <div className="mt-3 text-lg font-bold text-gray-900 leading-snug">{d.title}</div>
                  <div className="mt-1 text-sm text-gray-500 flex items-center gap-1">
                    {d.sub} <span className="transition-transform group-hover:translate-x-1">→</span>
                  </div>
                </button>
              ))}
            </div>

            <RotatingProfession />


          </div>
        )}

        {step === 'input' && (
          <div className="text-center">
            {loading ? (
              <div className="py-20 flex flex-col items-center">
                <div className="w-10 h-10 rounded-full border-[3px] border-gray-200 border-t-[#00aef7] animate-spin" />
                <p className="mt-5 text-gray-600 text-lg">
                  {website.trim() ? 'Reading your site and detecting your trade…' : 'Building your playbook…'}
                </p>
              </div>
            ) : (
              <>
                <button type="button" onClick={reset} className="text-sm text-gray-400 hover:text-gray-600 mb-6">← back</button>
                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">Let's tailor this to your business.</h1>
                <p className="mt-3 text-gray-500">Point us at your business online and our AI does the rest — or just tell us your trade.</p>

                {error && (
                  <div className="mt-5 max-w-xl mx-auto rounded-xl bg-red-50 ring-1 ring-red-200 text-red-700 text-sm px-4 py-3">
                    {error}
                  </div>
                )}

                <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="mt-6">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-center gap-3 max-w-2xl mx-auto text-left">
                    <div className="w-full sm:flex-1">
                      <input
                        autoFocus
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        placeholder="your website or social page"
                        className="w-full rounded-xl ring-1 ring-[#00aef7]/60 focus:ring-2 focus:ring-[#00aef7] px-5 py-3.5 text-base outline-none"
                      />
                      <p className="mt-2 text-xs text-gray-400 leading-relaxed">
                        Website, TikTok, Instagram, LinkedIn, Google Business — any page that shows what you do. We scan it and do most of your setup for you.
                      </p>
                    </div>
                    <span className="hidden sm:block text-sm text-gray-400 pt-4">or</span>
                    <div className="w-full sm:flex-1">
                      <input
                        value={profession}
                        onChange={(e) => setProfession(e.target.value)}
                        placeholder="your profession"
                        className="w-full rounded-xl ring-1 ring-gray-300 focus:ring-2 focus:ring-[#00aef7] px-5 py-3.5 text-base outline-none"
                      />
                      <p className="mt-2 text-xs text-gray-400 leading-relaxed">
                        Your industry or profession — be specific about your specialty. “Criminal defense attorney,” not just “lawyer.”
                      </p>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-white text-base font-semibold transition-transform hover:scale-[1.02]"
                    style={{ background: BRAND }}
                  >
                    Show me my playbook →
                  </button>
                </form>

                <PlatformRotation />
              </>
            )}
          </div>
        )}

        {step === 'playbook' && (
          <div>
            <button type="button" onClick={reset} className="text-sm text-gray-400 hover:text-gray-600 mb-5">← start over</button>

            {captured && captured.source === 'website' && (
              <div className="mb-5 rounded-xl bg-[#f0faff] ring-1 ring-[#00aef7]/25 px-5 py-4 text-left">
                {captured.businessName && (
                  <div className="text-sm text-gray-500">We read <span className="font-semibold text-gray-800">{captured.businessName}</span></div>
                )}
                <div className="mt-0.5 text-base text-gray-900">
                  Your industry:{' '}
                  <span className="font-bold" style={{ color: BRAND }}>
                    {captured.profession || "we couldn't tell — type your trade instead"}
                  </span>
                </div>
                <button type="button" onClick={reset} className="mt-1 text-xs text-gray-400 hover:text-gray-600 underline">
                  Not right? Start over
                </button>
              </div>
            )}

            <div className="flex items-center gap-3 mb-1.5">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white" style={{ background: BRAND }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden="true">
                  <rect x="8" y="3" width="8" height="4" rx="1" />
                  <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                  <path d="M9 12h6M9 16h4" />
                </svg>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: '#0784b8' }}>your game plan</div>
                <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900 leading-tight">The {displayTrade} playbook</h2>
              </div>
            </div>
            <p className="text-gray-500 text-sm">
              {door === 'ideas'
                ? <>Fresh plays for {displayTrade} — run them in order.</>
                : <>Run these plays in order — built for how {displayTrade} get found.</>}
            </p>

            {pb ? (
              <PlaybookPlays pb={pb} trade={displayTrade} door={door} />
            ) : (
              <div className="mt-5 rounded-xl bg-amber-50 ring-1 ring-amber-200 text-amber-800 text-sm px-4 py-3">
                We're putting your playbook together. Give it a moment and refresh — or finish setup and it'll be ready inside.
              </div>
            )}

            <div className="mt-8 text-center">
              <a
                href="/?modal=signup"
                className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-white text-lg font-semibold transition-transform hover:scale-[1.03] shadow-lg"
                style={{ background: BRAND }}
              >
                {door === 'ideas' ? 'Get fresh ideas + publish →' : 'Finish & publish this →'}
              </a>
              <div className="mt-3 text-sm text-gray-500">we design it, you hit publish — about 5 minutes</div>
              <div className="mt-4 text-xs text-gray-400">
                not ready? <span style={{ color: BRAND }} className="font-semibold cursor-pointer">email me my playbook</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
